import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2, ExternalLink, PhoneIncoming, Sparkles, StickyNote, Star, UserPlus, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useMutation, useQuery } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { useCrm, type CallPop } from './CrmContext';
import { LIFECYCLE_LABELS, type NumberLookup } from './types';
import { errorMessage, formatWhen, initials, phoneTypeFor, suggestionFields } from './format';
import { LookupSuggestion, TruecallerButton } from './LookupSuggestion';

/**
 * The pop-up for a number that is not a named contact: who it probably is (Unknown caller
 * look-up), and a quick "Who's calling?" form so whoever answers can name it during the call.
 */
function UnknownCaller({ pop }: { pop: CallPop }) {
  const { keepPop, openContact, openNewContact, data, hasFeature } = useCrm();
  const card = pop.card;
  const caller = card.known ? card.contact : null;
  const live = useQuery<NumberLookup | null>(
    backendApi.crmLookup.getLookup,
    (hasFeature('callerLookup') || hasFeature('truecallerLookup')) && pop.number ? { number: pop.number } : 'skip'
  );
  const lookup = live ?? card.lookup ?? null;
  const idName = pop.callerIdName ?? caller?.callerIdName;
  const suggestion = suggestionFields(lookup, idName);
  const convertCaller = useMutation(backendApi.crm.convertCaller);
  const saveContact = useMutation(backendApi.crm.saveContact);
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<{ contactId: string; name: string } | null>(null);

  const save = async () => {
    if (!name.trim()) return;
    keepPop(pop.callId);
    setSaving(true);
    try {
      const result = (
        caller
          ? await convertCaller({ contactId: caller.id, name, company: company.trim() || undefined })
          : await saveContact({ name, company: company.trim() || undefined, phones: [{ type: phoneTypeFor(pop.number), number: pop.number }] })
      ) as { contactId: string };
      setSaved({ contactId: result.contactId, name: name.trim() });
      toast.success(`Saved ${name.trim()}`);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full min-w-0 text-sm px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white';

  return (
    <>
      <div>
        <p className="text-lg font-semibold text-gray-900 dark:text-white">{saved?.name ?? idName ?? (pop.number || 'Unknown number')}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {!saved && idName ? `${pop.number} (name from caller ID) · ` : ''}
          {saved
            ? pop.number
            : caller
              ? `Not named yet · ${caller.stats.totalInteractions} earlier call${caller.stats.totalInteractions === 1 ? '' : 's'}`
              : 'Not in your contacts'}
          {!saved && pop.did ? ` · called ${pop.did}` : ''}
        </p>
      </div>

      {!saved && <LookupSuggestion lookup={lookup} />}
      {!saved && <TruecallerButton number={pop.number} lookup={lookup} />}

      {saved ? (
        <div className="space-y-2">
          <p className="flex items-center gap-1.5 text-sm text-green-700 dark:text-green-400">
            <CheckCircle2 className="w-4 h-4" /> Saved as a contact
          </p>
          <button
            type="button"
            onClick={() => openContact(saved.contactId)}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm"
          >
            <ExternalLink className="w-4 h-4" /> Open
          </button>
        </div>
      ) : (
        data?.canManage &&
        pop.number && (
          <form
            className="space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
            onFocus={() => keepPop(pop.callId)}
          >
            <p className="text-xs font-medium text-gray-700 dark:text-gray-200">Who's calling?</p>
            <div className="grid grid-cols-2 gap-2">
              <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Name" aria-label="Caller name" className={inputClass} />
              <input value={company} onChange={(event) => setCompany(event.target.value)} placeholder="Company" aria-label="Company" className={inputClass} />
            </div>
            {suggestion && !name && !company && (
              <button
                type="button"
                onClick={() => {
                  keepPop(pop.callId);
                  setName(suggestion.name);
                  setCompany(suggestion.company ?? '');
                }}
                className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
              >
                Use “{suggestion.name}”
              </button>
            )}
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={saving || !name.trim()}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white text-sm"
              >
                <UserPlus className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => {
                  keepPop(pop.callId);
                  if (caller) openContact(caller.id);
                  else openNewContact(pop.number);
                }}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {caller ? 'History' : 'More details'}
              </button>
            </div>
          </form>
        )
      )}
    </>
  );
}

function PopCard({ pop }: { pop: CallPop }) {
  const { dismissPop, keepPop, openContact, data } = useCrm();
  const addNote = useMutation(backendApi.crm.addNote);
  const [note, setNote] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const card = pop.card;
  const myCall = pop.answeredBy && pop.answeredBy === data?.prefs.extension;

  const status = pop.ended
    ? 'Call ended'
    : pop.answeredBy
      ? myCall
        ? 'You answered'
        : `Answered by ${pop.answeredBy}`
      : `Ringing ${pop.extensions.slice(0, 4).join(', ')}${pop.extensions.length > 4 ? '…' : ''}`;

  const saveNote = async () => {
    if (!card.known || !note.trim()) return;
    setSaving(true);
    try {
      await addNote({ contactId: card.contact.id, body: note });
      toast.success('Note saved');
      setNote('');
      setNoteOpen(false);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 40 }}
      onMouseEnter={() => keepPop(pop.callId)}
      className={`w-[22rem] max-w-[calc(100vw-2rem)] rounded-xl shadow-2xl border bg-white dark:bg-gray-800 overflow-hidden ${
        card.known && card.contact.vip ? 'border-amber-400' : 'border-gray-200 dark:border-gray-700'
      }`}
      role="alertdialog"
      aria-label="Incoming call"
    >
      <div className={`flex items-center gap-2 px-4 py-2 text-xs font-medium ${pop.ended ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300' : 'bg-green-600 text-white'}`}>
        <PhoneIncoming className={`w-4 h-4 ${pop.ended || pop.answeredBy ? '' : 'animate-pulse'}`} />
        <span className="flex-1 truncate">{status}</span>
        <button type="button" onClick={() => dismissPop(pop.callId)} className="opacity-80 hover:opacity-100" aria-label="Close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 space-y-3">
        {card.known && card.contact.kind === 'contact' ? (
          <>
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 shrink-0 rounded-full bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 flex items-center justify-center font-semibold">
                {initials(card.contact.name)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-gray-900 dark:text-white truncate">
                  {card.contact.vip && <Star className="inline w-4 h-4 mr-1 -mt-0.5 text-amber-500 fill-amber-400" />}
                  {card.contact.name}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                  {[card.contact.jobTitle, card.contact.company].filter(Boolean).join(' · ') || pop.number}
                </p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {card.contact.lifecycle && (
                    <span className="px-1.5 py-0.5 rounded bg-primary-50 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 text-[11px]">
                      {LIFECYCLE_LABELS[card.contact.lifecycle]}
                    </span>
                  )}
                  {card.contact.tags.slice(0, 3).map((tag) => (
                    <span key={tag} className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-[11px]">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
              <p>
                {card.contact.stats.totalInteractions} calls
                {card.lastSpokeWith &&
                  ` · last spoke ${formatWhen(card.lastSpokeWith.atMs).toLowerCase()} with ${card.lastSpokeWith.name ?? card.lastSpokeWith.extension}`}
              </p>
              {card.contact.ownerExtension && <p>Account owner: {card.contact.ownerName ?? card.contact.ownerExtension}</p>}
              {card.missedThisWeek > 0 && (
                <p className="flex items-center gap-1 text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Called {card.callsThisWeek}× this week, {card.missedThisWeek} missed
                </p>
              )}
            </div>

            {card.openTasks.length > 0 && (
              <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-2 text-xs text-amber-900 dark:text-amber-200">
                <p className="font-medium mb-1">Open tasks</p>
                {card.openTasks.slice(0, 2).map((task) => (
                  <p key={task.id} className="truncate">• {task.title}</p>
                ))}
              </div>
            )}
            {card.note && (
              <div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-2 text-xs text-gray-700 dark:text-gray-200">
                <p className="font-medium mb-0.5 flex items-center gap-1">
                  <StickyNote className="w-3 h-3" /> {card.note.pinned ? 'Pinned note' : 'Last note'} · {card.note.authorName}
                </p>
                <p className="line-clamp-3 whitespace-pre-line">{card.note.body}</p>
              </div>
            )}
            {card.summary && (
              <div className="rounded-lg bg-purple-50 dark:bg-purple-900/20 p-2 text-xs text-purple-900 dark:text-purple-200">
                <p className="font-medium mb-0.5 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Last call summary
                </p>
                <p className="line-clamp-3">{card.summary}</p>
              </div>
            )}

            {noteOpen && (
              <div className="space-y-2">
                <textarea
                  autoFocus
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  rows={3}
                  placeholder="What is this call about?"
                  className="w-full text-sm px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
                <button
                  type="button"
                  disabled={saving || !note.trim()}
                  onClick={() => void saveNote()}
                  className="w-full px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white text-sm"
                >
                  Save note
                </button>
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  keepPop(pop.callId);
                  openContact(card.contact.id);
                }}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm"
              >
                <ExternalLink className="w-4 h-4" /> Open
              </button>
              {data?.canManage && (
                <button
                  type="button"
                  onClick={() => {
                    keepPop(pop.callId);
                    setNoteOpen((open) => !open);
                  }}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <StickyNote className="w-4 h-4" /> Note
                </button>
              )}
            </div>
          </>
        ) : (
          <UnknownCaller pop={pop} />
        )}
      </div>
    </motion.div>
  );
}

/** Incoming-call pop-ups, top right, newest first. */
export function ScreenPopStack({ inline = false }: { inline?: boolean }) {
  const { pops } = useCrm();
  return (
    <div className={inline ? 'flex flex-col gap-3' : 'fixed top-20 right-4 z-[60] flex flex-col gap-3 pointer-events-none'}>
      <AnimatePresence>
        {pops.map((pop) => (
          <div key={pop.callId} className="pointer-events-auto">
            <PopCard pop={pop} />
          </div>
        ))}
      </AnimatePresence>
    </div>
  );
}
